
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';

import FormInput from '@/components/controls/form-input.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import Publish from '@/components/forms/publish.vue';
import ImageUploadControl from '@/components/controls/image-upload.vue';
import Autocomplete, { AutocompleteOption } from '@/components/controls/autocomplete.vue';
import LanguagePicker from '@/components/controls/language-picker.vue';

import { BannerStandImageStatus } from '@/store/modules/banner-stand-images/banner-stand-image';
import { BannerStandImagesActions } from '@/store/modules/banner-stand-images/types';

export default Vue.extend({
  name: 'EditBannerStandImageView',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    SubmitButton,
    NotificationBanner,
    Publish,
    ImageUploadControl,
    Autocomplete,
    LanguagePicker,
  },
  props: {
    add: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      productInput: '',
    };
  },
  computed: {
    ...mapState(['languages']),
    ...mapState('bannerStandImages', {
      item: 'editable',
      editableStatus: 'editableStatus',
      products: 'products',
      usedProducts: 'usedProducts',
      existingLanguages: 'existingLanguages',
    }),
    ...mapGetters('bannerStandImages', {
      isLoading: 'editableIsLoading',
      isSaving: 'editableIsSaving',
      hasUnsavedChanges: 'editableHasUnsavedChanges',
      availableProducts: 'availableProducts',
    }),
    languagesLoaded(): boolean {
      return this.languages && this.languages.length > 0;
    },
    isFetching(): boolean {
      return this.isLoading || this.isSaving;
    },
    isInactive(): boolean {
      return this.item?.status === BannerStandImageStatus.Inactive;
    },
    getProductOptions(): AutocompleteOption[] {
      return this.availableProducts
        .map((product: any) => ({
          label: product.title,
          value: product.id,
        }))
        .filter((option: AutocompleteOption) => 
          option.label.toLowerCase().indexOf(this.productInput.toLowerCase()) > -1
        );
    },
    getDisabledLanguages(): string[] {
      // When editing, disable languages that already exist for the current product
      // (except the current language being edited)
      if (this.item?.product?.id && this.existingLanguages.length > 0) {
        return this.existingLanguages.filter((lang: string) => lang !== this.item.languageCode);
      }
      return [];
    },
  },
  methods: {
    ...mapActions('bannerStandImages', {
      updateEditable: BannerStandImagesActions.UPDATE_EDITABLE,
      save: BannerStandImagesActions.SAVE,
      removeItem: BannerStandImagesActions.DELETE,
      restoreItem: BannerStandImagesActions.RESTORE,
      loadItem: BannerStandImagesActions.LOAD_EDITABLE,
      loadProducts: BannerStandImagesActions.LOAD_PRODUCTS,
      loadUsedProducts: BannerStandImagesActions.LOAD_USED_PRODUCTS,
      loadExistingLanguages: BannerStandImagesActions.LOAD_EXISTING_LANGUAGES,
    }),
    handleLanguageChange(languageCode: string) {
      this.updateEditable({ languageCode });
      // Reload used products for the new language
      this.loadUsedProducts({ languageCode });
    },
    selectProduct(option: AutocompleteOption) {
      const product = this.products.find((p: any) => p.id === option.value);
      if (!product) {
        return;
      }
      this.updateEditable({
        productId: product.id,
        product: { id: product.id, title: product.title },
      });
      // Load existing languages for this product to disable them
      this.loadExistingLanguages({ productId: product.id });
    },
    clearProduct() {
      this.updateEditable({
        productId: null,
        product: null,
      });
    },
    remove() {
      this.removeItem({ id: this.item.id });
    },
    restore() {
      this.restoreItem({ id: this.item.id });
    },
    async handleSave() {
      // Validate that a product is selected
      if (!this.item.product) {
        return;
      }
      // Validate that a language is selected
      if (!this.item.languageCode) {
        return;
      }
      const isValid = await this.$validator.validateAll();
      if (!isValid) {
        return;
      }
      this.save();
    },
  },
  created() {
    this.loadItem({ id: this.add ? undefined : this.$route.params.id }).then(() => {
      // After loading, if we're editing and have a product, load existing languages
      if (!this.add && this.item?.product?.id) {
        this.loadExistingLanguages({ productId: this.item.product.id });
      }
    });
    this.loadProducts();
    // Load used products for the current/default language
    this.loadUsedProducts({ languageCode: this.item?.languageCode || 'en' });
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm(this.$t('Do you really want to leave? You have unsaved changes!'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
});
